import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  AlbumCellProps,
  FolderBreadcrumbText,
} from '../../components/Molecules';
import { ClubSelectFolderPopup } from '../../components/Popups';
import { SwitchMemoryBoostPopup } from '../../components/Popups/SwitchMemoryBoostPopup';
import { HomeClubTemplate } from '../../components/Templates/Clubs';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { AlbumListType, SpaceListType } from '../../models/SpaceStore';
import {
  UpdateCurrentUserResult,
  WebBoosterType,
} from '../../models/UserStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

interface State {
  selectedBoosterType: WebBoosterType | undefined;
  partitionId: string;
  isLoading: boolean;
  isFinishedFetchSpace: boolean;
}

const { updateCurrentUser } = useUserStore.getState();
const { fetchSpace } = useSpaceStore.getState();

const HomeClubScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showPopup, hidePopup } = usePopup();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const [state, setState] = useState<State>({
    selectedBoosterType: userAccount?.settings.webBoosterType,
    partitionId: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.All}`,
    isLoading: true,
    isFinishedFetchSpace: false,
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      partitionId:
        partitionId ??
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`,
    }));
  }, [partitionId, state.selectedBoosterType]);

  // const updatedPartitionId = useMemo(() => {
  //   return (
  //     partitionId ?? `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
  //   );
  // }, [partitionId, state.selectedBoosterType]);

  useRedirectEntry();

  // MARK: Blick the first gameSet after creation
  const isJustFinishedCreate = useDraftStore(
    (store) => store.justFinishedCreate,
  );

  // MARK: Get Home Classroom
  const classroomId = activeUserId;
  useEffect(() => {
    if (classroomId) {
      fetchSpace(SpaceListType.Home, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId]);

  const classroom = useSpaceStore(
    (store) => store.getSpace(classroomId ?? ''),
    shallow,
  );

  const breadcrumbs = useMemo(() => {
    const folders =
      classroom?.getAllPartitions(userAccount?.settings.webBoosterType) ?? [];
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.partitionId,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [userAccount?.settings.webBoosterType, classroom, state.partitionId]);

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  // MARK: Get Albums
  const albumNormalFieldName = `${activeUserId}_${state.selectedBoosterType}_${state.partitionId}`;
  const albumNormalField = useSpaceStoreListField(
    'albums',
    albumNormalFieldName,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: albumNormalFieldName,
          classroomId: classroomId ?? '',
          webBoosterType: state.selectedBoosterType,
          partitionId:
            state.partitionId ===
            `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
              ? undefined
              : state.partitionId,
          withAlbumSummaries: true,
        },
        cursor,
      ),
    [
      albumNormalFieldName,
      classroomId,
      state.selectedBoosterType,
      state.partitionId,
    ],
  );

  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshAlbums,
    hasCursor,
  } = useFetchList(albumNormalField, fetchAlbums);

  useEffect(() => {
    if (classroomId) {
      refreshAlbums()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isLoading: false }));
        });
    }
  }, [refreshAlbums, classroomId]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const onClickAlbum = useCallback(
    (_classroomId: string, gameSetId: string) => {
      navigate(`/club/${_classroomId}/journey/${gameSetId}`, {
        state: { withBackState: true },
      });
    },
    [navigate],
  );

  const onShareAlbum = useCallback(
    (link: string) => {
      const pageUrl = window.location.href;

      const updatedUrl = new URL(pageUrl);
      updatedUrl.pathname = `${link}`;
      const finalUrl = decodeURIComponent(updatedUrl.toString()); // prevent ? => %3F
      if (navigator.share) {
        navigator.share({ url: finalUrl }).catch((err) => {
          console.error('Failed to share the URL: ', err);
        });
      } else {
        navigator.clipboard
          .writeText(finalUrl)
          .then(() => {
            toast(t('Link is copied!'), {
              ...AppToastInfoOption,
              id: 'clipboard',
            });
          })
          .catch((err) => {
            console.error('Failed to copy the URL: ', err);
          });
      }
    },
    [t],
  );

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(albumNormalFieldName);
  }, arrayEqual(shallow));

  const albumSummaries = useRecordStore((store) => {
    const gameSetIds = albums.map((item) => item.gameSetId);

    return gameSetIds.map((gameSetId) =>
      store.getAlbumSummary(gameSetId, activeUserId ?? ''),
    );
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    return albums.map((album) => {
      const isNoGame = album.statistics.gameCount === 0;
      const albumSummary = albumSummaries.find(
        (item) => item?.gameSetId === album.gameSetId,
      );
      return {
        album: album,
        albumClassroom: classroom,
        albumSummary: albumSummary,
        onClick: () => {
          isNoGame
            ? toast(t('Content is about to be unlocked.'), AppToastInfoOption)
            : onClickAlbum(album.classroomId, album.gameSetId);
        },
        onClickShare: () => {
          onShareAlbum(`/club/${album.classroomId}/journey/${album.gameSetId}`);
        },
      };
    });
  }, [albumSummaries, albums, classroom, onClickAlbum, onShareAlbum, t]);

  // MARK: Get Recent Albums
  const albumRecentField = useSpaceStoreListField(
    'albums',
    AlbumListType.Recent,
  );

  const fetchRecentAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore
        .getState()
        .fetchRecentAlbums({ classroomId: classroomId ?? '' }, cursor),
    [classroomId],
  );

  const { refresh: refreshRecentAlbums } = useFetchList(
    albumRecentField,
    fetchRecentAlbums,
  );

  useEffect(() => {
    if (classroomId) {
      refreshRecentAlbums().catch(console.error);
    }
  }, [refreshRecentAlbums, classroomId]);

  const recentAlbums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(AlbumListType.Recent);
  }, arrayEqual(shallow));

  const onClickRecentAlbum = useCallback(() => {
    navigate(`/club/${classroomId}/journey-recent`);
  }, [classroomId, navigate]);

  const onDefaultFinishCreation = useCallback(() => {
    useDraftStore.getState().setJustFinishedCreate(false);
  }, []);

  const onClickHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const onClickBreadcrumb = useCallback(
    (folder: string) => {
      if (classroom) {
        showPopup(
          <ClubSelectFolderPopup
            webBoosterType={userAccount?.settings.webBoosterType}
            space={classroom}
            selectedFolderId={folder}
            folders={classroom.getAllPartitions(
              userAccount?.settings.webBoosterType,
            )}
            newFolder={undefined}
            onClickSelect={onClickBreadcrumb}
            onCancel={hidePopup}
            onConfirm={() => {
              useSessionStore.getState().setPartitionId(folder);
              hidePopup();
            }}
          />,
        );
      }
    },
    [userAccount?.settings.webBoosterType, classroom, hidePopup, showPopup],
  );

  const onClickCreateGames = useCallback(() => {
    useDraftStore.getState().clear();
    navigate('/create/memory-game/select-type');
  }, [navigate]);

  const onSelectBooster = useCallback(
    (webBoosterType: WebBoosterType) => {
      const handleConfirm = async () => {
        if (userAccount) {
          const res = await updateCurrentUser({
            user: {
              settings: {
                ...userAccount.settings,
                webBoosterType: webBoosterType,
              },
            },
          });

          if (res === UpdateCurrentUserResult.Success) {
            setState((s) => ({ ...s, selectedBoosterType: webBoosterType }));
            useSessionStore.getState().setPartitionId(null);
          } else {
            // to-do error message
          }
        } else {
          // to-do error message
        }
      };
      hidePopup();
      handleConfirm().catch(console.error);
    },
    [userAccount, hidePopup],
  );

  const onClickSwitchBooster = useCallback(() => {
    showPopup(
      <SwitchMemoryBoostPopup
        selectedBooster={state.selectedBoosterType}
        onPrimary={hidePopup}
        onSelect={onSelectBooster}
      />,
    );
  }, [state.selectedBoosterType, hidePopup, onSelectBooster, showPopup]);

  return userProfile ? (
    <HomeClubTemplate
      userProfile={userProfile}
      webBoosterType={state.selectedBoosterType}
      breadcrumbs={breadcrumbs}
      hasRecentAlbum={recentAlbums.length > 0}
      albumData={albumData}
      isLoading={state.isLoading}
      hasCursor={hasCursor}
      isJustFinishedCreate={isJustFinishedCreate}
      onClickHome={onClickHome}
      onClickSwitchBooster={onClickSwitchBooster}
      onClickCreateGames={onClickCreateGames}
      onClickRecentAlbum={onClickRecentAlbum}
      onClickBreadcrumb={() => {
        onClickBreadcrumb(state.partitionId);
      }}
      loadMoreItems={loadMoreItems}
      onDefaultFinishCreation={onDefaultFinishCreation}
    />
  ) : null;
};

export default HomeClubScreen;
